// =============================================================================
// DATA MODELER STORE (ZUSTAND) - With React Flow integration
// =============================================================================

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  type Node,
  type Edge,
  type OnNodesChange,
  type OnEdgesChange,
  type OnConnect,
  type Connection,
} from '@xyflow/react';
import type { Entity, EntityField, Relation, DataModel, PrimitiveType } from './types';
import { DEFAULT_FIELDS } from './types';

interface DataModelerState {
  // Model state
  currentModel: DataModel | null;
  entities: Entity[];
  relations: Relation[];

  // React Flow state
  nodes: Node[];
  edges: Edge[];

  // UI state
  selectedEntityId: string | null;
  selectedRelationId: string | null;
  isDragging: boolean;
  zoom: number;

  // React Flow callbacks
  onNodesChange: OnNodesChange;
  onEdgesChange: OnEdgesChange;
  onConnect: OnConnect;

  // Actions
  setCurrentModel: (model: DataModel | null) => void;
  addEntity: (primitive: PrimitiveType, position: { x: number; y: number }) => Entity;
  updateEntity: (id: string, updates: Partial<Entity>) => void;
  removeEntity: (id: string) => void;
  duplicateEntity: (id: string) => void;

  // Field actions
  addField: (entityId: string, field: Omit<EntityField, 'id'>) => void;
  updateField: (entityId: string, fieldId: string, updates: Partial<EntityField>) => void;
  removeField: (entityId: string, fieldId: string) => void;
  reorderFields: (entityId: string, fromIndex: number, toIndex: number) => void;

  // Relation actions
  addRelation: (relation: Omit<Relation, 'id'>) => void;
  updateRelation: (id: string, updates: Partial<Relation>) => void;
  removeRelation: (id: string) => void;

  // Selection
  selectEntity: (id: string | null) => void;
  selectRelation: (id: string | null) => void;

  // View actions
  setIsDragging: (isDragging: boolean) => void;
  setZoom: (zoom: number) => void;

  // Sync helpers
  syncNodesToEntities: () => void;
  syncEdgesToRelations: () => void;

  // Persistence
  exportModel: () => DataModel;
  importModel: (model: DataModel) => void;
  reset: () => void;
}

const generateId = () => crypto.randomUUID();

// Helper to convert Entity to React Flow Node
function entityToNode(entity: Entity): Node {
  return {
    id: entity.id,
    type: 'entityNode',
    position: entity.position,
    data: { ...entity },
  };
}

// Helper to convert Relation to React Flow Edge
function relationToEdge(relation: Relation): Edge {
  return {
    id: relation.id,
    source: relation.sourceEntityId,
    target: relation.targetEntityId,
    type: 'relationEdge',
    data: {
      label: relation.name,
      relationType: relation.type,
    },
  };
}

export const useDataModelerStore = create<DataModelerState>()(
  immer((set, get) => ({
    // Initial state
    currentModel: null,
    entities: [],
    relations: [],
    nodes: [],
    edges: [],
    selectedEntityId: null,
    selectedRelationId: null,
    isDragging: false,
    zoom: 1,

    // React Flow callbacks
    onNodesChange: (changes) =>
      set((state) => {
        state.nodes = applyNodeChanges(changes, state.nodes);
        // Sync position changes back to entities
        for (const change of changes) {
          if (change.type === 'position' && change.position) {
            const entity = state.entities.find((e) => e.id === change.id);
            if (entity) {
              entity.position = change.position;
            }
          }
        }
      }),

    onEdgesChange: (changes) =>
      set((state) => {
        state.edges = applyEdgeChanges(changes, state.edges);
      }),

    onConnect: (connection: Connection) =>
      set((state) => {
        if (!connection.source || !connection.target) return;

        const relationId = generateId();
        const newRelation: Relation = {
          id: relationId,
          name: 'relation',
          sourceEntityId: connection.source,
          targetEntityId: connection.target,
          type: 'one-to-many',
        };

        state.relations.push(newRelation);
        state.edges = addEdge(
          {
            ...connection,
            id: relationId,
            type: 'relationEdge',
            data: {
              label: newRelation.name,
              relationType: newRelation.type,
            },
          },
          state.edges
        );
      }),

    // Model actions
    setCurrentModel: (model) =>
      set((state) => {
        state.currentModel = model;
        if (model) {
          state.entities = model.entities;
          state.relations = model.relations;
          state.nodes = model.entities.map(entityToNode);
          state.edges = model.relations.map(relationToEdge);
        }
      }),

    addEntity: (primitive, position) => {
      const defaultFields = DEFAULT_FIELDS[primitive] || [];
      const newEntity: Entity = {
        id: generateId(),
        name: `New${primitive.charAt(0).toUpperCase() + primitive.slice(1)}`,
        primitive,
        fields: defaultFields.map((f) => ({ ...f, id: generateId() })),
        position,
        color: getColorForPrimitive(primitive),
      };

      set((state) => {
        state.entities.push(newEntity);
        state.nodes.push(entityToNode(newEntity));
        state.selectedEntityId = newEntity.id;
      });

      return newEntity;
    },

    updateEntity: (id, updates) =>
      set((state) => {
        const entityIndex = state.entities.findIndex((e) => e.id === id);
        if (entityIndex !== -1 && state.entities[entityIndex]) {
          Object.assign(state.entities[entityIndex]!, updates);
          // Update the corresponding node
          const nodeIndex = state.nodes.findIndex((n) => n.id === id);
          if (nodeIndex !== -1 && state.nodes[nodeIndex]) {
            state.nodes[nodeIndex]!.data = { ...state.entities[entityIndex]! };
            if (updates.position) {
              state.nodes[nodeIndex]!.position = updates.position;
            }
          }
        }
      }),

    removeEntity: (id) =>
      set((state) => {
        state.entities = state.entities.filter((e) => e.id !== id);
        state.nodes = state.nodes.filter((n) => n.id !== id);
        state.relations = state.relations.filter(
          (r) => r.sourceEntityId !== id && r.targetEntityId !== id
        );
        state.edges = state.edges.filter(
          (e) => e.source !== id && e.target !== id
        );
        if (state.selectedEntityId === id) {
          state.selectedEntityId = null;
        }
      }),

    duplicateEntity: (id) => {
      const entity = get().entities.find((e) => e.id === id);
      if (!entity) return;

      const newEntity: Entity = {
        ...entity,
        id: generateId(),
        name: `${entity.name}Copy`,
        position: { x: entity.position.x + 50, y: entity.position.y + 50 },
        fields: entity.fields.map((f) => ({ ...f, id: generateId() })),
      };

      set((state) => {
        state.entities.push(newEntity);
        state.nodes.push(entityToNode(newEntity));
        state.selectedEntityId = newEntity.id;
      });
    },

    // Field actions
    addField: (entityId, field) =>
      set((state) => {
        const entity = state.entities.find((e) => e.id === entityId);
        if (entity) {
          entity.fields.push({ ...field, id: generateId() });
          // Update node data
          const node = state.nodes.find((n) => n.id === entityId);
          if (node) {
            node.data = { ...entity };
          }
        }
      }),

    updateField: (entityId, fieldId, updates) =>
      set((state) => {
        const entity = state.entities.find((e) => e.id === entityId);
        if (entity) {
          const fieldIndex = entity.fields.findIndex((f) => f.id === fieldId);
          if (fieldIndex !== -1 && entity.fields[fieldIndex]) {
            Object.assign(entity.fields[fieldIndex]!, updates);
            // Update node data
            const node = state.nodes.find((n) => n.id === entityId);
            if (node) {
              node.data = { ...entity };
            }
          }
        }
      }),

    removeField: (entityId, fieldId) =>
      set((state) => {
        const entity = state.entities.find((e) => e.id === entityId);
        if (entity) {
          entity.fields = entity.fields.filter((f) => f.id !== fieldId);
          // Update node data
          const node = state.nodes.find((n) => n.id === entityId);
          if (node) {
            node.data = { ...entity };
          }
        }
      }),

    reorderFields: (entityId, fromIndex, toIndex) =>
      set((state) => {
        const entity = state.entities.find((e) => e.id === entityId);
        if (entity && entity.fields[fromIndex]) {
          const [removed] = entity.fields.splice(fromIndex, 1);
          if (removed) entity.fields.splice(toIndex, 0, removed);
          // Update node data
          const node = state.nodes.find((n) => n.id === entityId);
          if (node) {
            node.data = { ...entity };
          }
        }
      }),

    // Relation actions
    addRelation: (relation) =>
      set((state) => {
        const newRelation = { ...relation, id: generateId() };
        state.relations.push(newRelation);
        state.edges.push(relationToEdge(newRelation));
      }),

    updateRelation: (id, updates) =>
      set((state) => {
        const index = state.relations.findIndex((r) => r.id === id);
        if (index !== -1 && state.relations[index]) {
          Object.assign(state.relations[index]!, updates);
          // Update the corresponding edge
          const edgeIndex = state.edges.findIndex((e) => e.id === id);
          if (edgeIndex !== -1 && state.edges[edgeIndex]) {
            const rel = state.relations[index]!;
            state.edges[edgeIndex]!.data = {
              label: rel.name,
              relationType: rel.type,
            };
          }
        }
      }),

    removeRelation: (id) =>
      set((state) => {
        state.relations = state.relations.filter((r) => r.id !== id);
        state.edges = state.edges.filter((e) => e.id !== id);
        if (state.selectedRelationId === id) {
          state.selectedRelationId = null;
        }
      }),

    // Selection
    selectEntity: (id) =>
      set((state) => {
        state.selectedEntityId = id;
        state.selectedRelationId = null;
      }),

    selectRelation: (id) =>
      set((state) => {
        state.selectedRelationId = id;
        state.selectedEntityId = null;
      }),

    setIsDragging: (isDragging) =>
      set((state) => {
        state.isDragging = isDragging;
      }),

    setZoom: (zoom) =>
      set((state) => {
        state.zoom = Math.max(0.1, Math.min(2, zoom));
      }),

    // Sync helpers
    syncNodesToEntities: () =>
      set((state) => {
        state.nodes = state.entities.map(entityToNode);
      }),

    syncEdgesToRelations: () =>
      set((state) => {
        state.edges = state.relations.map(relationToEdge);
      }),

    // Persistence
    exportModel: () => {
      const state = get();
      return {
        id: state.currentModel?.id || generateId(),
        name: state.currentModel?.name || 'Untitled Model',
        description: state.currentModel?.description,
        entities: state.entities,
        relations: state.relations,
        createdAt: state.currentModel?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },

    importModel: (model) =>
      set((state) => {
        state.currentModel = model;
        state.entities = model.entities;
        state.relations = model.relations;
        state.nodes = model.entities.map(entityToNode);
        state.edges = model.relations.map(relationToEdge);
        state.selectedEntityId = null;
        state.selectedRelationId = null;
      }),

    reset: () =>
      set((state) => {
        state.currentModel = null;
        state.entities = [];
        state.relations = [];
        state.nodes = [];
        state.edges = [];
        state.selectedEntityId = null;
        state.selectedRelationId = null;
        state.zoom = 1;
      }),
  }))
);

function getColorForPrimitive(primitive: PrimitiveType): string {
  const colors: Record<PrimitiveType, string> = {
    person: '#3B82F6',
    organization: '#8B5CF6',
    document: '#10B981',
    asset: '#F59E0B',
    transaction: '#EF4444',
    workflow: '#06B6D4',
    event: '#EC4899',
    location: '#84CC16',
  };
  return colors[primitive] || '#6B7280';
}
